import { useRouter } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { recoverFromStaleBuild } from "~/sdk/staleBuild";

/**
 * Router floor — port of `zeedog-tanstack/src/components/ui/RootErrorFallback.tsx`,
 * wired as `router.options.defaultErrorComponent` (`src/router.tsx`) for any
 * route that doesn't define its own `errorComponent`. No route on this site
 * defined one before: an uncaught throw (a section loader outside
 * `withSectionLoader`'s try/catch, a malformed `Route.useLoaderData()`
 * payload, etc.) bubbled to the top and produced a blank page in production,
 * with no way for the user to retry.
 *
 * Logs to console so error tracking / parity audits still see the real
 * error — this component is the safety net, not the diagnostic.
 */
export function RootErrorFallback({ error }: ErrorComponentProps) {
  const router = useRouter();

  if (typeof window !== "undefined") {
    console.error("[RootErrorFallback]", error);
    // Previous build's chunk vanished on deploy: reload instead of showing
    // an error on a navigation that has nothing wrong with it. See
    // `sdk/staleBuild.ts`.
    recoverFromStaleBuild(error);
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold mb-2">Algo deu errado</h1>
        <p className="text-sm opacity-60 mb-6">
          Ocorreu um erro ao carregar esta página. Tente novamente.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => router.invalidate()}
        >
          Tentar de novo
        </button>
      </div>
    </div>
  );
}

export default RootErrorFallback;
